import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { friendlyProviderName } from '@/lib/providerLabels';
import { Button } from '@/components/ui/button';
import HelpTooltip from '@/components/help/HelpTooltip';
import {
  Activity, Eye, Radio, Cpu, ArrowRight, RefreshCw,
  CheckCircle2, XCircle, AlertTriangle, Clock, Filter,
  ChevronDown, Search, Wifi, WifiOff, Minus, ThumbsUp, ThumbsDown
} from 'lucide-react';

const STATUS_COLORS = {
  success: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: CheckCircle2 },
  failed: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: XCircle },
  pending: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: Clock },
  skipped: { bg: 'bg-slate-50', text: 'text-slate-500', border: 'border-slate-200', icon: Minus },
  info: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: Activity },
};

const CHANNEL_LABELS = { whatsapp: 'WhatsApp', voice: 'Voz', email: 'Email', unknown: 'Otro' };
const MODULE_LABELS = { marketing: 'Marketing', automation: 'Automatizaciones', voice: 'Voz', inbox: 'Inbox', ai: 'IA', integrations: 'Integraciones', unknown: 'Otro' };
const SOURCE_LABELS = { integration_log: 'Log integración', messaging_event: 'Evento messaging', campaign_attempt: 'Delivery campaña', automation_action: 'Acción automática', voice_call: 'Llamada voz', usage_event: 'Evento de uso' };
const HEALTH_COLORS = {
  healthy: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Saludable' },
  warning: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Advertencia' },
  failing: { bg: 'bg-red-50', text: 'text-red-700', label: 'Fallando' },
  not_configured: { bg: 'bg-slate-50', text: 'text-slate-400', label: 'Sin configurar' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_COLORS[status] || STATUS_COLORS.info;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${cfg.bg} ${cfg.text} ${cfg.border} border`} data-testid={`status-badge-${status}`}>
      <Icon className="w-3 h-3" /> {status}
    </span>
  );
}

function KpiCard({ label, value, icon: Icon, color = '#0F172A' }) {
  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 flex items-center gap-3" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + '12' }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div>
        <p className="text-xs text-[#94A3B8] font-medium">{label}</p>
        <p className="text-xl font-bold text-[#0F172A]">{value ?? '-'}</p>
      </div>
    </div>
  );
}

function ChannelBar({ channel, count, total }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2 text-sm" data-testid={`channel-bar-${channel}`}>
      <span className="w-20 text-[#64748B] text-xs font-medium truncate">{CHANNEL_LABELS[channel] || channel}</span>
      <div className="flex-1 h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
        <div className="h-full bg-[#0F172A] rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-[#0F172A] font-semibold w-8 text-right">{count}</span>
    </div>
  );
}

// ── Overview Tab ──
function OverviewTab({ overview }) {
  if (!overview) return null;
  return (
    <div className="space-y-5" data-testid="overview-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Total eventos" value={overview.total_events} icon={Activity} color="#0F172A" />
        <KpiCard label="Exitosos" value={overview.success_count} icon={CheckCircle2} color="#059669" />
        <KpiCard label="Fallidos" value={overview.failed_count} icon={XCircle} color="#DC2626" />
        <KpiCard label="Latencia media" value={overview.avg_duration_ms ? `${overview.avg_duration_ms}ms` : '-'} icon={Clock} color="#D97706" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-4" data-testid="channel-distribution">
          <h3 className="text-sm font-semibold text-[#0F172A] mb-3">Distribución por canal</h3>
          <div className="space-y-2">
            {Object.entries(overview.events_by_channel || {}).length > 0 ?
              Object.entries(overview.events_by_channel).map(([ch, cnt]) => (
                <ChannelBar key={ch} channel={ch} count={cnt} total={overview.total_events} />
              )) : <p className="text-xs text-[#94A3B8]">Sin datos en este rango</p>}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-4" data-testid="failure-reasons">
          <h3 className="text-sm font-semibold text-[#0F172A] mb-3">Top razones de fallo</h3>
          {overview.top_failure_reasons?.length > 0 ? (
            <div className="space-y-1">
              {overview.top_failure_reasons.map(([reason, cnt], i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <XCircle className="w-3.5 h-3.5 text-red-500 mt-0.5 shrink-0" />
                  <span className="text-[#64748B] flex-1 break-all">{reason}</span>
                  <span className="text-[#0F172A] font-semibold">{cnt}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-xs text-[#94A3B8]">Sin fallos en este rango</p>}
        </div>
      </div>
    </div>
  );
}

// ── Events Timeline Tab ──
function EventsTab() {
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ source_type: '', channel: '', status: '', q: '' });
  const [detail, setDetail] = useState(null);
  const [showTechDetail, setShowTechDetail] = useState(false);

  const load = useCallback(async (nextCursor = null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '30');
      if (nextCursor) params.set('cursor', nextCursor);
      if (filters.source_type) params.set('source_type', filters.source_type);
      if (filters.channel) params.set('channel', filters.channel);
      if (filters.status) params.set('status', filters.status);
      if (filters.q) params.set('q', filters.q);
      const { data } = await api.get(`/tenant/observability/events?${params}`);
      if (nextCursor) {
        setEvents(prev => [...prev, ...data.items]);
      } else {
        setEvents(data.items || []);
      }
      setCursor(data.next_cursor);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = async (eventId) => {
    try {
      const { data } = await api.get(`/tenant/observability/events/${eventId}`);
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="events-tab">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center" data-testid="events-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <input type="text" placeholder="Buscar eventos..." value={filters.q}
            onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
            className="w-full pl-8 pr-3 py-2 text-sm border border-[#E2E8F0] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0F172A]"
            data-testid="events-search" />
        </div>
        <select value={filters.source_type} onChange={e => setFilters(f => ({ ...f, source_type: e.target.value }))}
          className="text-xs border border-[#E2E8F0] rounded-lg px-2.5 py-2 bg-white text-[#0F172A]" data-testid="filter-source-type">
          <option value="">Todas las fuentes</option>
          {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filters.channel} onChange={e => setFilters(f => ({ ...f, channel: e.target.value }))}
          className="text-xs border border-[#E2E8F0] rounded-lg px-2.5 py-2 bg-white text-[#0F172A]" data-testid="filter-channel">
          <option value="">Todos los canales</option>
          {Object.entries(CHANNEL_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
          className="text-xs border border-[#E2E8F0] rounded-lg px-2.5 py-2 bg-white text-[#0F172A]" data-testid="filter-status">
          <option value="">Todos los estados</option>
          <option value="success">Exitoso</option>
          <option value="failed">Fallido</option>
          <option value="pending">Pendiente</option>
          <option value="skipped">Omitido</option>
          <option value="info">Info</option>
        </select>
      </div>

      {/* Event list */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden" data-testid="events-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#64748B]">Fecha</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#64748B]">Fuente</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#64748B]">Canal</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#64748B]">Estado</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#64748B]">Resumen</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#64748B]">Proveedor</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {events.length === 0 && !loading ? (
              <tr><td colSpan={7} className="text-center py-8 text-xs text-[#94A3B8]">Sin eventos en este rango</td></tr>
            ) : events.map((ev, i) => (
              <tr key={ev.event_id || i} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC] cursor-pointer transition-colors"
                onClick={() => loadDetail(ev.event_id)} data-testid={`event-row-${i}`}>
                <td className="px-4 py-2 text-xs text-[#64748B] whitespace-nowrap">{ev.occurred_at ? new Date(ev.occurred_at).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '-'}</td>
                <td className="px-4 py-2 text-xs text-[#0F172A]">{SOURCE_LABELS[ev.source_type] || ev.source_type}</td>
                <td className="px-4 py-2 text-xs">{CHANNEL_LABELS[ev.channel] || ev.channel}</td>
                <td className="px-4 py-2"><StatusBadge status={ev.status} /></td>
                <td className="px-4 py-2 text-xs text-[#64748B] max-w-[200px] truncate">{ev.summary || '-'}</td>
                <td className="px-4 py-2 text-xs text-[#0F172A]">{ev.provider_key ? friendlyProviderName(ev.provider_key) : '-'}</td>
                <td className="px-4 py-2"><ArrowRight className="w-3.5 h-3.5 text-[#94A3B8]" /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <div className="flex justify-center py-4"><div className="w-6 h-6 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" /></div>}
        {cursor && !loading && (
          <div className="flex justify-center py-3 border-t border-[#E2E8F0]">
            <Button variant="ghost" size="sm" onClick={() => load(cursor)} data-testid="load-more-events">
              <ChevronDown className="w-4 h-4 mr-1" /> Cargar más
            </Button>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setDetail(null)} data-testid="event-detail-modal">
          <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-[#0F172A]">Detalle del evento</h3>
              <button onClick={() => setDetail(null)} className="text-[#94A3B8] hover:text-[#0F172A]" data-testid="close-detail">X</button>
            </div>
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-[#94A3B8]">ID:</span> <span className="text-[#0F172A] font-mono">{detail.event_id}</span></div>
                <div><span className="text-[#94A3B8]">Fuente:</span> {SOURCE_LABELS[detail.source_type] || detail.source_type}</div>
                <div><span className="text-[#94A3B8]">Canal:</span> {CHANNEL_LABELS[detail.channel] || detail.channel}</div>
                <div><span className="text-[#94A3B8]">Módulo:</span> {MODULE_LABELS[detail.module] || detail.module}</div>
                <div><span className="text-[#94A3B8]">Estado:</span> <StatusBadge status={detail.status} /></div>
                <div><span className="text-[#94A3B8]">Dirección:</span> {detail.direction || '-'}</div>
                <div><span className="text-[#94A3B8]">Proveedor:</span> {detail.provider_key ? friendlyProviderName(detail.provider_key) : '-'}</div>
                <div><span className="text-[#94A3B8]">Latencia:</span> {detail.duration_ms ? `${detail.duration_ms}ms` : '-'}</div>
              </div>
              <div><span className="text-[#94A3B8]">Resumen:</span> <p className="text-[#0F172A] mt-0.5">{detail.summary || '-'}</p></div>
              {detail.error_message && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                  <span className="text-red-700 font-medium">Error:</span> <span className="text-red-600">{detail.error_message}</span>
                </div>
              )}
              <div className="text-[#94A3B8]">Fecha: {detail.occurred_at ? new Date(detail.occurred_at).toLocaleString('es-MX') : '-'}</div>
              {(detail.capability_key || detail.integration_instance_id || (detail.metadata && Object.keys(detail.metadata).length > 0)) && (
                <div className="border-t border-[#F1F5F9] pt-2">
                  <button onClick={() => setShowTechDetail(v => !v)} className="text-[#94A3B8] hover:text-[#0F172A] underline" data-testid="toggle-tech-detail">
                    {showTechDetail ? 'Ocultar' : 'Ver'} detalle técnico
                  </button>
                  {showTechDetail && (
                    <div className="mt-2 space-y-2">
                      {detail.capability_key && <div><span className="text-[#94A3B8]">Capability:</span> <code className="text-[#0F172A] bg-[#F1F5F9] px-1 py-0.5 rounded">{detail.capability_key}</code></div>}
                      {detail.integration_instance_id && <div><span className="text-[#94A3B8]">Instance ID:</span> <code className="text-[#0F172A] bg-[#F1F5F9] px-1 py-0.5 rounded text-[10px]">{detail.integration_instance_id}</code></div>}
                      {detail.metadata && Object.keys(detail.metadata).length > 0 && (
                        <div>
                          <span className="text-[#94A3B8]">Metadata:</span>
                          <pre className="mt-1 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-2 text-[10px] overflow-x-auto max-h-40">{JSON.stringify(detail.metadata, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Channel Health Tab ──
function ChannelHealthTab() {
  const [health, setHealth] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/tenant/observability/channel-health')
      .then(r => setHealth(r.data || []))
      .catch(e => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <PantallaCargando />;

  return (
    <div className="space-y-4" data-testid="channel-health-tab">
      {health.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-8 text-center">
          <WifiOff className="w-8 h-8 text-[#94A3B8] mx-auto mb-2" />
          <p className="text-sm text-[#64748B]">No hay instancias de canal configuradas</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {health.map((ch, i) => {
            const hc = HEALTH_COLORS[ch.health_status] || HEALTH_COLORS.not_configured;
            return (
              <div key={i} className={`bg-white rounded-xl border border-[#E2E8F0] p-4 ${ch.health_status === 'failing' ? 'ring-1 ring-red-200' : ''}`} data-testid={`health-card-${i}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Wifi className={`w-4 h-4 ${ch.connection_status === 'connected' ? 'text-emerald-500' : 'text-red-400'}`} />
                    <span className="text-sm font-semibold text-[#0F172A]">{ch.instance_label || friendlyProviderName(ch.provider_key)}</span>
                  </div>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${hc.bg} ${hc.text}`}>{hc.label}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-[#94A3B8]">Proveedor:</span> <span className="text-[#0F172A]">{friendlyProviderName(ch.provider_key)}</span></div>
                  <div><span className="text-[#94A3B8]">Canal:</span> <span className="text-[#0F172A]">{ch.channel_type || '-'}</span></div>
                  <div><span className="text-[#94A3B8]">Fallos 24h:</span> <span className={ch.failure_count_24h > 0 ? 'text-red-600 font-semibold' : 'text-[#0F172A]'}>{ch.failure_count_24h}</span></div>
                  <div><span className="text-[#94A3B8]">Omitidos 24h:</span> <span className="text-[#0F172A]">{ch.skipped_count_24h}</span></div>
                  <div><span className="text-[#94A3B8]">Último éxito:</span> <span className="text-[#0F172A]">{ch.last_success_at ? new Date(ch.last_success_at).toLocaleString('es-MX', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}</span></div>
                  <div><span className="text-[#94A3B8]">Último fallo:</span> <span className="text-[#0F172A]">{ch.last_failure_at ? new Date(ch.last_failure_at).toLocaleString('es-MX', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}</span></div>
                </div>
                {ch.enabled_capabilities?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {ch.enabled_capabilities.map(c => (
                      <span key={c} className="text-[10px] bg-[#F1F5F9] text-[#64748B] px-1.5 py-0.5 rounded">{c}</span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── AI Observability Tab (WP12: AIRequestLog + feedback) ──
function AITab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedbackBusy, setFeedbackBusy] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/tenant/observability/ai?limit=50')
      .then(r => setData(r.data))
      .catch(e => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const setFeedback = async (logId, rating) => {
    setFeedbackBusy(logId + rating);
    try {
      await api.post(`/tenant/observability/ai-requests/${logId}/feedback`, { rating });
      toast.success(rating === 'up' ? 'Marcada como respuesta útil' : 'Marcada como respuesta incorrecta');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setFeedbackBusy(null); }
  };

  if (loading && !data) return <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" /></div>;

  const items = data?.items || [];

  if (!items.length) {
    return (
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-8 text-center" data-testid="ai-empty">
        <Cpu className="w-8 h-8 text-[#94A3B8] mx-auto mb-2" />
        <p className="text-sm text-[#64748B]">{data?.empty_state_message || 'Sin actividad de IA'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="ai-tab">
      <div className="p-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg text-xs text-[#64748B] flex items-center gap-2">
        <span>Marca cada respuesta de IA como útil o incorrecta para mejorar tu biblioteca y habilidades.</span>
        <HelpTooltip text="Esto no reentrena el modelo automáticamente: solo guarda tu opinión para que tú o tu equipo conviertan buenas respuestas en FAQs o habilidades reutilizables." />
      </div>
      <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden" data-testid="ai-requests-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#64748B]">Fecha</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#64748B]">Canal</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#64748B]">Respuesta</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#64748B]">Estado</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#64748B]">
                <span className="inline-flex items-center gap-1">Tu opinión <HelpTooltip text="👍 = la respuesta fue correcta. 👎 = estuvo mal o incompleta. Puedes cambiar tu voto cuando quieras." /></span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((row, i) => (
              <tr key={row.id || i} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]" data-testid={`ai-request-row-${i}`}>
                <td className="px-4 py-2 text-xs text-[#64748B] whitespace-nowrap">
                  {row.created_at ? new Date(row.created_at).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                </td>
                <td className="px-4 py-2 text-xs">{CHANNEL_LABELS[row.channel] || row.channel || '-'}</td>
                <td className="px-4 py-2 text-xs text-[#64748B] max-w-[240px] truncate" title={row.response_preview}>{row.response_preview || row.prompt_preview || '-'}</td>
                <td className="px-4 py-2"><StatusBadge status={row.status === 'success' ? 'success' : 'failed'} /></td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setFeedback(row.id, 'up')}
                      disabled={feedbackBusy === row.id + 'up'}
                      title="Respuesta correcta"
                      className={`p-1.5 rounded-lg border transition-colors ${row.feedback_rating === 'up' ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'border-[#E2E8F0] text-[#94A3B8] hover:text-emerald-600'}`}
                      data-testid={`feedback-up-${row.id}`}
                    >
                      <ThumbsUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setFeedback(row.id, 'down')}
                      disabled={feedbackBusy === row.id + 'down'}
                      title="Respuesta incorrecta"
                      className={`p-1.5 rounded-lg border transition-colors ${row.feedback_rating === 'down' ? 'bg-red-50 border-red-300 text-red-700' : 'border-[#E2E8F0] text-[#94A3B8] hover:text-red-600'}`}
                      data-testid={`feedback-down-${row.id}`}
                    >
                      <ThumbsDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main Page ──
const TABS = [
  { key: 'overview', label: 'Resumen', icon: Eye },
  { key: 'events', label: 'Timeline', icon: Activity },
  { key: 'health', label: 'Salud de canales', icon: Radio },
  { key: 'ai', label: 'IA', icon: Cpu },
];

export default function ObservabilityCenter() {
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/tenant/observability/overview')
      .then(r => setOverview(r.data))
      .catch(e => {
        if (e.response?.status === 403) {
          toast.error('Observabilidad no habilitada en tu plan.');
        } else {
          toast.error(formatApiError(e.response?.data?.detail));
        }
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <PantallaCargando data-testid="observability-loading" />;

  return (
    <div className="space-y-5" data-testid="observability-center">
      <div className="p-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg text-xs text-[#64748B]">
        Vista técnica avanzada — para revisar el estado general de tu negocio, ve a la pestaña "Estado".
      </div>
      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-[#E2E8F0] overflow-x-auto" data-testid="observability-tabs">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
              tab === t.key ? 'border-[#0F172A] text-[#0F172A]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`}
            data-testid={`obs-tab-${t.key}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab overview={overview} />}
      {tab === 'events' && <EventsTab />}
      {tab === 'health' && <ChannelHealthTab />}
      {tab === 'ai' && <AITab />}
    </div>
  );
}
